import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    EntityActionFactory,
    EntityCollectionService,
    EntityOp,
    EntityServices
} from '@ngrx/data';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import {
    debounce,
    debounceTime,
    delay,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    map,
    share,
    skip,
    startWith,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import clone from 'just-clone';
import { LoggingService } from '../../../shared/services/logging';
import * as fromDataServices from '../../../data-services';
import { Process, State } from '../../../shared';
import { ActionsSubject, Store, select } from '@ngrx/store';
import * as fromShared from '../../../shared';
import * as tp from '../../third-party/store';
import * as fromUser from '../../../user-info';
import { ScheduledDate } from '../../../lookups';
import * as fromScheduledDates from '../../../lookups/store/schedule-date.selectors';
import * as fromSettings from '../../../company-setting';
import moment from 'moment';
import { ofType } from '@ngrx/effects';
import { ToasterService } from '../../../toaster';
import { PayrollExportModel } from '../../../data-services/payroll/payroll-export';
@Component({
    selector: 'ef-payroll-export-container',
    templateUrl: './payroll-export-container.component.html',
    styleUrls: ['./payroll-export-container.component.scss'],
    standalone: false
})
export class PayrollExportContainerComponent implements OnInit, OnDestroy {
    userInfo$: Observable<fromUser.IUserInfo>;
    process$: Observable<Process>;
    PayrollExportModelDS: EntityCollectionService<fromDataServices.PayrollExport.PayrollExportModel>;
    PayrollExportModel$: Observable<
        fromDataServices.PayrollExport.PayrollExportModel[]
    >;
    payPeriods$: Observable<
        fromDataServices.PayrollExport.PayrollExportModel[]
    >;
    payPeriodDates$: Observable<ScheduledDate[]>;
    payPeriodDatesSelectItems$: Observable<fromShared.SelectItem[]>;
    selectedPayPeriod$: Observable<ScheduledDate | undefined>;
    export$: Observable<tp.ThirdPartyPayroll[]>;
    ViewPayPeriod = new ReplaySubject<string | number>(1);
    SendPayPeriod = new ReplaySubject<string | number>(1);
    SyncGL = new ReplaySubject<string | number>(1);
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    fileFormats$: Observable<any[]>;
    selectedErrorWithStatus$: Observable<fromShared.IStatusMessage | undefined>;
    private active = true;
    moment: any = moment;
    showTimer = false;
    processingText = 'Processing';
    constructor(
        private log: LoggingService,
        private store: Store,
        public entityServices: EntityServices,
        private af: EntityActionFactory,
        private dispatcher: ActionsSubject,
        private toaster: ToasterService
    ) {
        this.log.verbose({ type: 'PayrollExportContainerComponent::' });
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.PayrollExportModelDS =
            entityServices.getEntityCollectionService<fromDataServices.PayrollExport.PayrollExportModel>(
                fromDataServices.PayrollExport.entityName
            );
        this.store.dispatch(tp.getFileFormat());

        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'PayrollExportFormat'
            })
        );
    }
    ngOnInit(): void {
        this.PayrollExportModelDS.clearCache();
        this.PayrollExportModelDS.getAll();
        this.setupObservables();
        this.setupSubscriptions();
    }
    setupObservables() {
        this.PayrollExportModel$ = this.PayrollExportModelDS.entities$.pipe(
            map(items => clone(items))
        );
        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));
        this.selectedErrorWithStatus$ = this.store.pipe(
            select(fromShared.Errors.selectErrorWithStatus)
        );
        this.payPeriodDates$ = this.store.pipe(
            select(
                fromScheduledDates.selectScheduledDateType({
                    ScheduledDateType: 'PPE'
                })
            ),
            tap(items =>
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:payPeriodDates$',
                    items
                })
            )
        );
        this.payPeriodDatesSelectItems$ = this.payPeriodDates$.pipe(
            map(dates =>
                dates.map(d => ({
                    label: `${moment(d.ScheduledStartDate).format(
                        'MM/DD/YY'
                    )} - ${moment(d.ScheduledEndDate).format('MM/DD/YY')}`,
                    value: d.ScheduledDateID
                }))
            )
        );
        this.export$ = this.store.pipe(
            select(tp.selectAll),
            map(x => clone(x) as tp.ThirdPartyPayroll[])
        );
        this.payPeriods$ = combineLatest([
            this.payPeriodDates$,
            this.PayrollExportModel$
        ]).pipe(
            takeWhile(() => this.active),
            map(x => {
                x[1] = x[1].filter(p => (p.id as number) >= 0);
                return x;
            }),
            map(([dates, items]) => {
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:payPeriods$:status',
                    dates,
                    items
                });
                const ret = dates.map(date => {
                    const status = items.find(
                        item =>
                            moment(item.PayPeriodEndDate).unix() ===
                            moment(date.ScheduledEndDate).unix()
                    );
                    if (status)
                        this.log.verbose({
                            type: 'PayrollExportContainerComponent:payPeriods$:status',
                            status,
                            date
                        });
                    return {
                        id: status?.id ?? date.ScheduledDateID,
                        gcpid: status?.id,
                        ppid: date.ScheduledDateID,
                        PayPeriodEndDate: date.ScheduledEndDate,
                        SentDate: status?.SentDate ?? undefined,
                        SentBy: status?.SentBy ?? undefined,
                        ExportStatus: this.splitPascalCase(
                            status?.ExportStatus || 'NotExported'
                        ),
                        ValidationMessages: status?.ValidationMessages || [],
                        Status: status?.Status || {
                            StatusCode: fromShared.ResponseStatus.None,
                            Message: ''
                        },
                        canView: true,
                        canSend: this.canSend(status?.ExportStatus),
                        canSync: this.canSync(status?.ExportStatus),
                        canRefresh: this.canRefresh(status?.ExportStatus)
                    };
                });
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:payPeriods$:status',
                    ret
                });
                return ret;
            })
        );
        let seq = 0;
        this.fileFormats$ = this.store.pipe(
            select(tp.selectFileFormats),
            map(x => clone(x) as tp.FileFormatModel[]),
            tap(x => (seq = x.reduce((a, c) => Math.max(a, c.Sequencenbr), 0))),
            map(x =>
                x.map(y => {
                    if (y.Sequencenbr) {
                        seq = y.Sequencenbr + 1;
                    }
                    return { ...y, sequence: y.Sequencenbr || seq };
                })
            ),
            map(x => x.sort((a, b) => a.Sequencenbr - b.Sequencenbr))
        );
        this.selectedPayPeriod$ = this.ViewPayPeriod.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.payPeriodDates$),
            map(([id, dates]) => dates.find(x => x.ScheduledDateID === id))
        );
    }
    setupSubscriptions() {
        this.log.verbose({
            type: 'PayrollExportContainerComponent:setupSubscriptions'
        });
        combineLatest([this.process$, this.ShowErrorSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
        //this turns the loading overlay off when all items are completed or not exported
        this.PayrollExportModel$.pipe(
            takeWhile(() => this.active),
            tap(items =>
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:PayrollExportModel$:complete:1',
                    items
                })
            ),
            // first 2 are 0, 1 from initialization, 1 from the ClearAll
            skip(2),
            tap(items => {
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:PayrollExportModel$:complete:2',
                    items
                });
                // this.showTimer = true;
            }),
            map(items => items.filter(item => (item.id as number) >= 0)),
            // filter(items => !!items && items.length > 0),
            filter(items =>
                items.every(
                    item =>
                        (Object as any)
                            .values(
                                fromDataServices.PayrollExport
                                    .PayrollExportCompletedStatuses
                            )
                            .includes(item.ExportStatus) ||
                        item.ExportStatus === 'Not Exported'
                )
            )
        ).subscribe(items => {
            this.log.verbose({
                type: 'PayrollExportContainerComponent:PayrollExportModel$:complete:3',
                items
            });
            this.showTimer = false;
        });

        // this just sends out seterrors for any validation messages
        this.PayrollExportModel$.pipe(
            takeWhile(() => this.active),
            map(items =>
                items.filter(
                    item =>
                        !(Object as any)
                            .values(
                                fromDataServices.PayrollExport
                                    .PayrollExportCompletedSuccessStatuses
                            )
                            .includes(item.ExportStatus)
                )
            ),
            map(items =>
                items.filter(
                    item =>
                        item.ValidationMessages &&
                        item.ValidationMessages.length > 0
                )
            ),
            filter(items => items.length > 0),
            tap(items =>
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:PayrollExportModel$:validationmessages',
                    items
                })
            )
        ).subscribe(items => {
            this.store.dispatch(new fromShared.Errors.ClearErrors());
            items.map(item => {
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:PayrollExportModel$:validationmessages:dispatch',
                    items
                });
                this.store.dispatch(
                    new fromShared.Errors.SetErrors(
                        item.ValidationMessages.map(x => ({
                            ...x,
                            MsgEntityID: item.id,
                            MsgProcessID: fromShared.ProcessEnum.PayrollExport,
                            StatusCode:
                                fromShared.ResponseStatus[
                                    x.severity as keyof typeof fromShared.ResponseStatus
                                ],
                            Message: `${moment(item.PayPeriodEndDate).format(
                                'L'
                            )} - ${x.messageText}`
                        }))
                    )
                );
            });
        });

        //should set this up in setupObservables, but it's only used here
        const incomplete$ = this.PayrollExportModel$.pipe(
            takeWhile(() => this.active),
            filter(items => items.length > 0),
            tap(items =>
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:PayrollExportModel$:incomplete:1',
                    items
                })
            ),
            map(items => items.filter(p => (p.id as number) >= 0)),
            //filter out any that have a success status
            map(items =>
                items.filter(
                    item =>
                        !(Object as any)
                            .values(
                                fromDataServices.PayrollExport
                                    .PayrollExportCompletedSuccessStatuses
                            )
                            .includes(item.ExportStatus)
                )
            ),
            tap(items =>
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:PayrollExportModel$:incomplete:2',
                    items
                })
            ),
            //filter out any that have a completed status
            map(items =>
                items.filter(
                    item =>
                        !(Object as any)
                            .values(
                                fromDataServices.PayrollExport
                                    .PayrollExportCompletedStatuses
                            )
                            .includes(item.ExportStatus)
                )
            ),
            tap(items =>
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:PayrollExportModel$:incomplete:3',
                    items
                })
            ),
            filter(items => items.length > 0)
        );

        //this calls api to fetch the status of any in process exports
        incomplete$.pipe(debounceTime(5000)).subscribe(items => {
            this.processingText = 'Processing';
            this.log.verbose({
                type: 'PayrollExportContainerComponent:incomplete$',
                items
            });
            items.forEach(item => {
                this.store.dispatch(
                    tp.fetchPayrollExportStatus({ id: item.id })
                );
            });
        });

        this.ViewPayPeriod.pipe(
            takeWhile(() => this.active),
            distinctUntilChanged(),
            withLatestFrom(this.payPeriodDates$)
        ).subscribe(([id, payDates]) => {
            const pd = payDates.find(x => x.ScheduledDateID === id);
            if (pd) {
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:ViewPayPeriod',
                    id,
                    pd
                });
                this.store.dispatch(tp.clearThirdPartyPayrolls());
                this.store.dispatch(
                    tp.getPayrollExport({
                        codeAsRow: true,
                        startDate: pd.ScheduledStartDate,
                        endDate: pd.ScheduledEndDate
                    })
                );
            }
        });

        this.SendPayPeriod.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.payPeriodDates$),
            withLatestFrom(this.PayrollExportModel$),
            tap(([[id, payDates], exps]) =>
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:SendPayPeriod',
                    id,
                    payDates,
                    exps
                })
            )
        ).subscribe(([[id, payDates], exps]) => {
            const pd = payDates.find(x => x.ScheduledDateID === id);
            const e = exps.find(
                x =>
                    moment(x.PayPeriodEndDate).format('L') ===
                    moment(pd?.ScheduledEndDate).format('L')
            );
            this.log.verbose({
                type: 'PayrollExportContainerComponent:SendPayPeriod',
                id,
                pd,
                e
            });
            if (pd) {
                this.store.dispatch(
                    tp.sendPayrollExport({
                        id: e?.id,
                        endDate: pd.ScheduledEndDate
                    })
                );
            }
            if (e) {
                const a1 = this.af.create(
                    fromDataServices.PayrollExport.entityName,

                    EntityOp.SAVE_DELETE_ONE_SUCCESS,
                    e.id
                );
                this.store.dispatch(a1);
            }
            this.showTimer = true;
        });
        this.SyncGL.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.payPeriodDates$),
            withLatestFrom(this.PayrollExportModel$),
            tap(([[id, payDates], exps]) =>
                this.log.verbose({
                    type: 'PayrollExportContainerComponent:SyncPayPeriod',
                    id,
                    payDates,
                    exps
                })
            )
        ).subscribe(([[id, payDates], exps]) => {
            const pd = payDates.find(x => x.ScheduledDateID === id);
            const e = exps.find(
                x =>
                    moment(x.PayPeriodEndDate).format('L') ===
                    moment(pd?.ScheduledEndDate).format('L')
            );
            this.log.verbose({
                type: 'PayrollExportContainerComponent:SyncGL',
                id,
                pd,
                e
            });
            if (pd) {
                this.store.dispatch(
                    tp.syncGL({
                        id: e?.id,
                        endDate: pd.ScheduledEndDate
                    })
                );
            }
            if (e) {
                // if (update.changes.SyncPayrollItems === true) {
                const a1 = this.af.create(
                    fromDataServices.PayrollExport.entityName,
                    // EntityOp.SAVE_UPSERT_ONE_SUCCESS,
                    // {
                    //     ...e,
                    //     // ExportStatus: 'Processing',
                    //     ValidationMessages: null
                    // }
                    EntityOp.SAVE_DELETE_ONE_SUCCESS,
                    e.id
                );
                // this.store.dispatch(a1);
            }
            this.showTimer = true;
        });

        this.dispatcher
            .pipe(
                takeWhile(() => this.active),
                ofType(tp.syncGLFailure),
                tap(result =>
                    this.log.verbose({
                        type: 'PayrollExportContainerComponent:syncGLFailure',
                        result
                    })
                )
            )
            .subscribe(() => {
                this.showTimer = false;
            });

        this.dispatcher
            .pipe(
                takeWhile(() => this.active),
                ofType(tp.syncGLSuccess),
                tap(result =>
                    this.log.verbose({
                        type: 'PayrollExportContainerComponent:syncGLSuccess',
                        result
                    })
                )
            )
            .subscribe(() => {
                this.showTimer = false;
            });
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    canSend = (status: string | undefined) =>
        (Object as any)
            .values(fromDataServices.PayrollExport.PayrollExportErrorStatuses)
            .includes(status) ||
        !(Object as any)
            .values(
                fromDataServices.PayrollExport.PayrollExportCompletedStatuses
            )
            .includes(status);
    canSync = (status: string | undefined) =>
        (Object as any)
            .values(
                fromDataServices.PayrollExport
                    .PayrollExportCompletedSuccessStatuses
            )
            .includes(status);
    canRefresh = (status: string | undefined) =>
        !!status &&
        !(
            (Object as any)
                .values(
                    fromDataServices.PayrollExport
                        .PayrollExportCompletedSuccessStatuses
                )
                .includes(status) ||
            (Object as any)
                .values(
                    fromDataServices.PayrollExport
                        .PayrollExportInProgressStatuses
                )
                .includes(status)
        );
    onView($event) {
        this.log.verbose({
            type: 'PayrollExportContainerComponent:onView',
            $event
        });
        this.ViewPayPeriod.next($event.ppid ?? $event.id);
    }
    onSend($event: string | number) {
        this.log.verbose({
            type: 'PayrollExportContainerComponent:onSend',
            $event
        });
        this.showTimer = true;
        this.SendPayPeriod.next($event);
    }
    onSync($event: string | number) {
        this.log.verbose({
            type: 'PayrollExportContainerComponent:onSync',
            $event
        });
        this.showTimer = true;
        this.SyncGL.next($event);
    }
    onRefresh($event: PayrollExportModel) {
        this.log.verbose({
            type: 'PayrollExportContainerComponent:onRefresh',
            $event
        });

        this.PayrollExportModelDS.updateOneInCache({
            ...$event,
            ExportStatus:
                fromDataServices.PayrollExport.PayrollExportInProgressStatuses
                    .Refreshing
        });
        this.store.dispatch(tp.fetchPayrollExportStatus({ id: $event.id }));
    }
    splitPascalCase = word => {
        const wordRe = /($[a-z])|[A-Z][^A-Z]+/g;
        return word.match(wordRe).join(' ');
    };
}
